import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";

export interface UseGetAllSsqOptions {
    page?: number;
    itemsPerPage?: number;
    enabled?: boolean;
    entityType?: "company" | "product" | "packaging";
    publishedOnly?: boolean;
}

export const useGetAllSsq = (options: UseGetAllSsqOptions = {}) => {
    const { page = 1, itemsPerPage = 200, enabled = true, entityType, publishedOnly = false } = options;

    const query = useQuery({
        queryKey: ["getAllSsq", page, itemsPerPage, entityType, publishedOnly],
        queryFn: async () => {
            const params: Record<string, any> = {
                page,
                itemsPerPage,
            };

            if (entityType) {
                params["ssqTargetEntities.entityType"] = entityType;
            }

            if (publishedOnly) {
                params["exists[currentVersion]"] = true;
            }

            return await sureApiCall.get("/api/ssqs", {
                params,
            });
        },
        select: (response) => {
            if (!response || !response.data) {
                return {
                    ssqs: [],
                    totalItems: 0,
                };
            }

            const ssqs = response.data["hydra:member"] || [];
            const totalItems = response.data["hydra:totalItems"] || 0;

            const data = zodParseWithLog(ssqs, SsqAutocompleteSchema, "getAllSsq");

            return {
                ssqs: data,
                totalItems,
            };
        },
        enabled,
        staleTime: 30000, // Cache for 30 seconds
        cacheTime: 300000, // Keep in cache for 5 minutes
    });

    return {
        ssqs: query.data?.ssqs || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};

// Schema ultra-permissif pour l'autocomplete
const SsqAutocompleteSchema = z.array(z.any()).transform((items) => {
    return items
        .map((item: any) => ({
            id: item.id || 0,
            title: item.title || "Sans titre",
            displayTitle: item.displayTitle || null,
            ...item, // Garde toutes les autres propriétés
        }))
        .filter((item: any) => typeof item.id === "number" && typeof item.title === "string");
});

// Schema complet pour d'autres utilisations
const SsqHeadingSchema = z.object({
    "@context": z.string().optional(),
    "@id": z.string().optional(),
    "@type": z.string().optional(),
    title: z.string(),
});

const SsqSsqHeadingSchema = z.object({
    ssqHeading: SsqHeadingSchema,
    hidden: z.boolean().optional(),
});

export const SsqListSchema = z
    .object({
        id: z.number(),
        title: z.string(),
        ssqSsqHeadings: SsqSsqHeadingSchema.array().optional(),
        parentSsqSsqHeadings: SsqSsqHeadingSchema.array().optional(),
    })
    .array();

export type SsqList = z.infer<typeof SsqListSchema>;
